<script lang="ts" generics="S">
  /**
   * AlgorithmPlayer — reusable shell that plays back any algorithm step list.
   *
   * Owns playback state (current index, playing, speed). Renders controls,
   * a Melt UI scrubber, and the current step's narration. Calls the
   * `visual` snippet with the current step so the algorithm-specific
   * rendering can read off it.
   *
   * When `waitForVisual` is true, the player awaits a promise (provided via
   * onStepReady callback) before auto-advancing. This lets visuals with
   * multi-part animations (e.g. sequential Rubik's cube moves) control timing.
   */
  import { onDestroy } from 'svelte';
  import { createSlider, melt } from '@melt-ui/svelte';
  import type { Snippet } from 'svelte';
  import type { StepEnvelope, PlaybackSpeed, PlayerOptions } from './types';
  import NarrationCard from './primitives/NarrationCard.svelte';

  type Props = {
    steps: ReadonlyArray<StepEnvelope<S>>;
    visual: Snippet<[StepEnvelope<S>, () => void]>;
    options?: PlayerOptions;
  };

  let { steps, visual, options = {} }: Props = $props();

  const loop = $derived(options.loop ?? false);
  const waitForVisual = $derived(options.waitForVisual ?? false);
  const baseStepMs = $derived(options.stepDurationMs ?? 900);

  function getInitialSpeed(): PlaybackSpeed {
    return options.initialSpeed ?? 1;
  }

  let playing = $state(false);
  let speed: PlaybackSpeed = $state(getInitialSpeed());

  // --- Step ready signaling ---
  // The visual calls onStepReady() when its animation is complete.
  // In waitForVisual mode, the play loop awaits waitForReady().
  //
  // Race condition: onStepReady may fire BEFORE waitForReady is called
  // (e.g. for non-animated steps). readyFired remembers this.
  let resolveReady: (() => void) | null = null;
  let readyFired = false;

  function onStepReady() {
    if (resolveReady) {
      resolveReady();
      resolveReady = null;
    } else {
      readyFired = true;
    }
  }

  function waitForReady(): Promise<void> {
    if (readyFired) {
      readyFired = false;
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      resolveReady = resolve;
    });
  }

  // --- Melt UI scrubber ---
  const {
    elements: { root: sliderRoot, range: sliderRange, thumbs: sliderThumbs },
    states: { value: sliderValue },
    options: { max: sliderMax },
  } = createSlider({
    defaultValue: [0],
    min: 0,
    max: 1,
    step: 1,
  });

  const lastIndex = $derived(Math.max(0, steps.length - 1));
  const currentIndex = $derived($sliderValue[0] ?? 0);
  const safeIndex = $derived(Math.min(currentIndex, lastIndex));
  const currentStep = $derived(steps[safeIndex]);

  $effect(() => { sliderMax.set(lastIndex); });

  function setIndex(next: number): void {
    const clamped = Math.max(0, Math.min(next, lastIndex));
    sliderValue.set([clamped]);
  }

  $effect(() => {
    if (currentIndex > lastIndex) sliderValue.set([lastIndex]);
  });

  // --- Auto-play loop (async) ---
  let playAbort: AbortController | null = null;

  async function playLoop() {
    playAbort = new AbortController();
    const { signal } = playAbort;

    while (!signal.aborted) {
      // Reset ready state before advancing
      readyFired = false;
      resolveReady = null;

      // Advance first, then wait for the visual to finish
      if (currentIndex >= lastIndex) {
        if (loop) {
          setIndex(0);
        } else {
          playing = false;
          break;
        }
      } else {
        setIndex(currentIndex + 1);
      }

      if (signal.aborted) break;

      // Wait for the visual's animation to complete before advancing again
      if (waitForVisual) {
        await waitForReady();
      } else {
        await new Promise<void>((r) => {
          const id = setTimeout(r, baseStepMs / speed);
          signal.addEventListener('abort', () => { clearTimeout(id); r(); }, { once: true });
        });
      }
    }
  }

  function startPlayback(): void {
    if (playing) return;
    if (currentIndex >= lastIndex && !loop) setIndex(0);
    playing = true;
    playLoop();
  }

  function pausePlayback(): void {
    playing = false;
    playAbort?.abort();
    playAbort = null;
    resolveReady = null;
  }

  function togglePlay(): void {
    if (playing) pausePlayback();
    else startPlayback();
  }

  function stepForward(): void {
    pausePlayback();
    setIndex(currentIndex + 1);
  }

  function stepBack(): void {
    pausePlayback();
    setIndex(currentIndex - 1);
  }

  function restart(): void {
    pausePlayback();
    setIndex(0);
  }

  function cycleSpeed(): void {
    speed = (speed === 1 ? 2 : speed === 2 ? 4 : 1) as PlaybackSpeed;
  }

  // --- Keyboard shortcuts ---
  function handleKeydown(e: KeyboardEvent): void {
    const target = e.target as HTMLElement | null;
    const tag = target?.tagName?.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return;

    if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      togglePlay();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      stepForward();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      stepBack();
    } else if (e.key === 'Home') {
      e.preventDefault();
      restart();
    }
  }

  onDestroy(() => {
    playAbort?.abort();
  });
</script>

<svelte:window onkeydown={handleKeydown} />

<div class="player">
  <div class="visual-stage">
    {#if currentStep}
      {@render visual(currentStep, onStepReady)}
    {/if}
  </div>

  <NarrationCard text={currentStep?.narration ?? ''} />

  <div class="controls">
    <button
      type="button"
      class="ctrl ctrl-play"
      onclick={togglePlay}
      aria-label={playing ? 'Pause' : 'Play'}
    >
      {#if playing}
        <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
          <rect x="2" y="1" width="3.5" height="12" fill="currentColor" />
          <rect x="8.5" y="1" width="3.5" height="12" fill="currentColor" />
        </svg>
      {:else}
        <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
          <path d="M3 1 L12 7 L3 13 Z" fill="currentColor" />
        </svg>
      {/if}
      <span>{playing ? 'Pause' : 'Play'}</span>
    </button>

    <button type="button" class="ctrl" onclick={stepBack} aria-label="Step back">
      <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
        <path d="M10 2 L4 7 L10 12 Z M3 2 L3 12" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </button>

    <button type="button" class="ctrl" onclick={stepForward} aria-label="Step forward">
      <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
        <path d="M4 2 L10 7 L4 12 Z M11 2 L11 12" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </button>

    <button type="button" class="ctrl ctrl-restart" onclick={restart} aria-label="Restart">
      <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
        <path d="M2 7 a5 5 0 1 0 1.5-3.5 M2 1.5 L2 4 L4.5 4" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </button>

    <span use:melt={$sliderRoot} class="slider">
      <span use:melt={$sliderRange} class="slider-range"></span>
      <span use:melt={$sliderThumbs[0]} class="slider-thumb" aria-label="Scrub"></span>
    </span>

    <span class="step-counter">{currentIndex + 1} / {steps.length}</span>

    <button type="button" class="ctrl ctrl-speed" onclick={cycleSpeed} aria-label="Playback speed">
      {speed}x
    </button>
  </div>
</div>

<style>
  .player {
    display: flex;
    flex-direction: column;
    gap: var(--space-md);
    width: 100%;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    padding: var(--space-lg);
  }

  .visual-stage {
    min-height: 260px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-md);
  }

  .controls {
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    padding-top: var(--space-md);
    border-top: 1px solid var(--border);
  }

  .ctrl {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 6px 10px;
    color: var(--ink-muted);
    font-family: var(--font-sans);
    font-size: var(--text-xs);
    font-weight: var(--weight-medium);
    cursor: pointer;
    transition:
      border-color 0.12s ease,
      color 0.12s ease,
      background 0.12s ease;
    min-width: 32px;
    min-height: 32px;
  }

  .ctrl:hover {
    border-color: var(--border-strong);
    color: var(--ink);
  }

  .ctrl-play {
    background: var(--accent);
    color: var(--bg);
    border-color: var(--accent);
    padding: 6px 14px;
  }

  .ctrl-play:hover {
    background: var(--accent-hover);
    border-color: var(--accent-hover);
    color: var(--bg);
  }

  .ctrl-speed {
    font-variant-numeric: tabular-nums;
    min-width: 40px;
  }

  .slider {
    position: relative;
    flex: 1;
    height: 20px;
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  .slider::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: 3px;
    background: var(--border);
    border-radius: 2px;
    transform: translateY(-50%);
  }

  .slider-range {
    position: absolute;
    height: 3px;
    background: var(--accent);
    border-radius: 2px;
    top: 50%;
    transform: translateY(-50%);
  }

  .slider-thumb {
    display: block;
    width: 14px;
    height: 14px;
    background: var(--bg);
    border: 2px solid var(--accent);
    border-radius: 50%;
    cursor: grab;
    position: relative;
    z-index: 1;
    transition: transform 0.12s ease;
  }

  .slider-thumb:hover,
  .slider-thumb:focus {
    transform: scale(1.15);
    outline: none;
  }

  .step-counter {
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    color: var(--ink-muted);
    font-variant-numeric: tabular-nums;
    min-width: 50px;
    text-align: right;
  }

  @media (max-width: 600px) {
    .player {
      padding: var(--space-md);
    }
    .controls {
      flex-wrap: wrap;
    }
    .slider {
      order: 10;
      flex-basis: 100%;
      margin-top: var(--space-sm);
    }
    .step-counter {
      order: 11;
    }
  }
</style>
